import { ActionTree } from 'vuex';
import { AxiosError, AxiosResponse } from 'axios';
import { fetchPosts, fetchRegion, loadRedirects } from '@/api';
import { SearchResult, StoreActions, StoreMutations } from '@/templates/DataNames';
import { RegionData, RootState } from '../types';
import { TreeData } from '@/templates/Tree';
import { getUserCountryCode } from '@/heplers/getUserCountryCode';
import {
  filterContentFromFullContent,
  filterContentFromSearch,
  filterContentFromTree,
} from '@/heplers/filterContentByCountry';
import { normalizeRedirects } from '@/heplers/redirects';

export const getCookieValue = (name: string, cookieHeader?: string): string => {
  // If cookieHeader is provided (from server), use it
  if (cookieHeader) {
    const cookie = cookieHeader.split(';').find((c) => c.trim().startsWith(`${name}=`));
    if (cookie) {
      return cookie.split('=')[1];
    }
  }

  // On the client, use document.cookie
  if (typeof document !== 'undefined') {
    const cookie = document.cookie.split(';').find((c) => c.trim().startsWith(`${name}=`));
    if (cookie) {
      return cookie.split('=')[1];
    }
  }
};

export let fullContent: TreeData = {} as TreeData;

export const actions: ActionTree<RootState, RootState> = {
  [StoreActions.REDIRECTS]: async ({ commit }): Promise<void> => {
    const redirects = await loadRedirects();
    commit(StoreMutations.REDIRECTS, normalizeRedirects(redirects));
  },
  [StoreActions.TREE]: async ({ commit, state }, { cookie, ssrContext }): Promise<void> => {
    const url = `getTree`;
    const isClient = typeof window !== 'undefined';

    const savedValue = isClient
      ? getCookieValue('with_by_link') || state.withByLink // client: cookie or Vuex (for SPA)
      : getCookieValue('with_by_link', cookie); // server: ONLY cookie from headers
    const withByLink = savedValue ? { with_by_link: String(savedValue) } : null;

    try {
      const response = await fetchPosts(url, withByLink);
      const tree = response.data;
      const countryCode = await getUserCountryCode(ssrContext);
      const filteredTreeData = filterContentFromTree(tree, countryCode);
      commit(StoreMutations.TREE, filteredTreeData);
    } catch (error) {
      console.error('Failed to fetch and process tree data');
    }
  },

  [StoreActions.COUNTRY_CODE]: async ({ commit }): Promise<void> => {
    const response: AxiosResponse<RegionData> = await fetchRegion();
    commit(StoreMutations.COUNTRY_CODE, response?.data.country_code?.toLowerCase() || null);
  },

  [StoreActions.SEARCH]: async ({ commit }, data: string): Promise<void> => {
    const url = `searchDoc${data.trim()}`;
    const fromCookie = getCookieValue('with_by_link');
    const withByLink = fromCookie ? { with_by_link: fromCookie } : null;

    const response: AxiosResponse<SearchResult> = await fetchPosts(url, withByLink);
    const countryCode = await getUserCountryCode(null);
    const filteredData = filterContentFromSearch(response.data, countryCode);
    commit(StoreMutations.SEARCH, filteredData);
  },

  [StoreActions.CONTENT]: async (
    { state, commit },
    { cookie, ssrContext, ...props }
  ): Promise<void> => {
    const p = props
      ? '?' +
        Object.entries(props)
          .map((pair) => pair.join('='))
          .join('&')
      : '';
    const url = `getDoc${p}`;
    const isClient = typeof window !== 'undefined';

    const savedValue = isClient
      ? getCookieValue('with_by_link') || state.withByLink // client: cookie or Vuex (for SPA)
      : getCookieValue('with_by_link', cookie); // server: ONLY cookie from headers
    const withByLink = savedValue ? { with_by_link: String(savedValue) } : null;

    state.contentLoading = true;

    try {
      const response = await fetchPosts(url, withByLink);
      fullContent = response.data;
      const countryCode = await getUserCountryCode(ssrContext);
      const filteredFullContentData = filterContentFromFullContent(fullContent, countryCode);
      commit(StoreMutations.CONTENT, filteredFullContentData);
    } catch (error) {
      if ((error as AxiosError).response?.data?.status === 404) {
        state.notFound = true;
      }
    }
  },
};
